/*
 * POST /api/v1/videos — the call that creates a job.
 *
 * Split out of `content/services/videoApi.ts`, which kept it next to the job
 * *lifecycle* calls (status, resume, confirm, cancel). That put the one
 * function needing `fileForUpload` and the model/duration defaults inside the
 * content slice, so content imported generation while generation imported
 * content for this very function: a two-module cycle, and the reason neither
 * slice could be extracted or chunked on its own.
 *
 * Creating a video is generation's job. Watching one is content's.
 */
import type { GenerationMetadata, ProcessingStatus, SubmissionPayload } from '@/shared/types';
import { postJson, postMultipart, type RequestOptions } from '@/shared/api/apiClient';

export const CLIENT_CAPABILITY_HEADER = 'X-Valoi-Client-Capabilities';
export const REVIEW_CLIENT_CAPABILITY = 'reference-review';
import { fileForUpload } from '@/features/generation/services/fileExtraction';
import { resolveDurationSeconds, resolveModelName } from '@/features/generation/utils/generationDefaults';
import {
  CREATE_VIDEO_PATH,
  IDENTITY_FROM_JWT,
  LIBRARY_BACKEND,
  normalizeStatus,
} from '@/shared/api/backendContract';

// Declared here as well as in videoApi: POST /videos and POST /videos/:id/resume
// are different endpoints that happen to answer in the same shape today, and
// sharing one declaration would tie them together for no reason beyond that.
type GenerateApiResponse = {
  job_id: string;
  status: ProcessingStatus;
};

export type GenerateJobResult = {
  jobId: string;
  status: ProcessingStatus;
  etaSeconds: number;
  requestedDurationSeconds: number;
  aspectRatio: string;
  metadata: GenerationMetadata;
};

export type RequestVideoGenerationOptions = {
  idempotencyKey?: string;
};

// Model/duration defaults live in utils/generationDefaults — they drive pricing,
// so any caller must resolve them through that module, never locally.
const DEFAULT_SCENES: number | null = null;
const DEFAULT_ASPECT_RATIO = '16:9';
const DEFAULT_USER_ID = 'valoi-web';

const estimateEtaSeconds = (text: string) => {
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  if (words === 0) return 90;
  return Math.max(60, Math.min(420, Math.round(words * 1.4)));
};

// Scalar fields shared by the JSON and multipart encodings. `text_input` and the
// uploaded `file` are mutually exclusive and added separately by the caller.
interface GenerateRequestFields {
  // Omitted once the backend derives identity from the JWT (IDENTITY_FROM_JWT).
  user_id?: string;
  story_id: null;
  model_name: string;
  duration: number;
  aspect_ratio: string;
  max_scenes: number | null;
  session_id?: string;
  image_guidance?: boolean;
  title?: string;
  author?: string;
  // The chosen VISUAL style for Wan-rendered videos ("storybook"/"photoreal", plus
  // "documentation" from the CLI and paper routes); ignored by Kling/LTX.
  // See app/datatypes.py::VideoStyle.
  style?: string;
  // How the words are delivered — a separate axis from style. Omitted for the engine's
  // default ("storytelling"); "non-narrative" is what makes the characters speak their
  // own lines and what makes the engine charge for that audio.
  // See app/datatypes.py::VideoMode.
  mode?: string;
}

// Encodes the scalar fields plus the uploaded document as multipart/form-data.
// The backend reads each scalar off the form (coercing "" / "null" to None) and
// derives text_input by parsing the file, so we omit null/undefined fields and
// never send text_input here.
function toFormData(file: File, fields: GenerateRequestFields): FormData {
  const formData = new FormData();
  // Stamp the canonical Content-Type for the extension so the backend's strict
  // MIME check passes even when the browser reported an empty/octet-stream type.
  formData.append('file', fileForUpload(file));
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    formData.append(key, String(value));
  }
  return formData;
}

export async function requestVideoGeneration(
  payload: SubmissionPayload,
  options: RequestVideoGenerationOptions = {},
): Promise<GenerateJobResult> {
  // Identity must come from the authenticated user, never the title — a title
  // fallback would scope the job under a bogus per-title "user" and hide it from
  // the real owner's library. (Dropped entirely once IDENTITY_FROM_JWT flips.)
  const userId = payload.userId || DEFAULT_USER_ID;

  const modelName = resolveModelName(payload.modelName);
  // Wan ignores this field for RENDERING and always produces a fixed 6s/scene (see
  // app/llm/wan.py::WAN_CLIP_DURATION_S), which is why sending 10 here used to look
  // merely cosmetic. It is not: the engine also prices from this field, so the value
  // sent here decides the hold — hence the shared helper rather than a local rule.
  const duration = resolveDurationSeconds(modelName, payload.duration);
  const aspectRatio = payload.aspectRatio || DEFAULT_ASPECT_RATIO;
  const maxScenes = payload.maxScenes !== undefined ? payload.maxScenes : DEFAULT_SCENES;

  const fields: GenerateRequestFields = {
    title: payload.title,
    story_id: null,
    // Wan is the default engine for new videos (matches the backend's
    // GenerateRequest.model_name default) — Kling/LTX remain reachable via the debug
    // panel's Model Name override.
    model_name: modelName,
    duration,
    aspect_ratio: aspectRatio,
    max_scenes: maxScenes,
  };

  // The style picker — a real, always-visible control (unlike the debug-only fields
  // below), sent unconditionally so the backend always knows the user's choice. Passed
  // through verbatim: the union that says which values are legal lives on
  // SubmissionPayload, so a new style needs no change here.
  if (payload.style) {
    fields.style = payload.style;
  }

  // The narration control. Only sent when it differs from the engine's default, so a
  // narrated submission is byte-identical to what it was before the control existed.
  // It needs its own line rather than riding on `style`: they are separate axes, and a
  // payload field that no one forwards is dropped in silence — spreading it into the
  // object literal skips TypeScript's excess-property check, so nothing would have
  // caught it.
  if (payload.mode) {
    fields.mode = payload.mode;
  }

  // Free attribution (accepts an optional author). Only sent when we actually
  // have a display name to avoid posting an empty string.
  if (payload.author) {
    fields.author = payload.author;
  }

  // Legacy client-supplied identity. Inactive now that IDENTITY_FROM_JWT=true (the
  // backend scopes by the Bearer JWT); kept behind the flag as a fast revert path.
  if (!IDENTITY_FROM_JWT) {
    fields.user_id = userId;
  }

  if (payload.sessionId) {
    fields.session_id = payload.sessionId;
  }

  if (payload.imageGuidance) {
    fields.image_guidance = true;
  }

  // Kling is the only supported engine. The 'videos' backend exposes a single
  // POST /api/v1/videos; the legacy 'video' backend uses /api/v1/visualize.
  const endpoint = LIBRARY_BACKEND === 'videos' ? CREATE_VIDEO_PATH : '/api/v1/visualize';
  const requestOptions: RequestOptions = {
    headers: {
      // Tells the engine this build can show the character review and approve it, so a
      // job may be paused for review. Not a user setting: it is sent on every
      // submission by every build that ships the review panel, and clients that omit it
      // (the CLI, older builds) are never paused. The engine's rollout flag decides
      // whether a job is actually paused.
      [CLIENT_CAPABILITY_HEADER]: REVIEW_CLIENT_CAPABILITY,
      ...(options.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {}),
    },
  };
  // POST /videos takes exactly one of file or text_input: upload sends the raw
  // document as multipart (the backend parses it), paste sends JSON text_input.
  const response = payload.file
    ? await postMultipart<GenerateApiResponse>(endpoint, toFormData(payload.file, fields), requestOptions)
    : await postJson<GenerateApiResponse>(endpoint, { ...fields, text_input: payload.text ?? '' }, requestOptions);

  return {
    jobId: response.job_id,
    // Normalize so a create response using a status synonym (e.g. 'succeeded')
    // is classified consistently with the polling/library paths.
    status: normalizeStatus(response.status),
    // No word count for an uploaded file (the backend extracts the text); fall
    // back to estimateEtaSeconds' empty-text default.
    etaSeconds: estimateEtaSeconds(payload.text ?? ''),
    /* Per scene, NOT the total: this lands in ProcessingJob.durationSeconds,
       which hydrateJob documents as "the requested seconds *per scene*" and
       reads back from metadata.duration. It used to be multiplied by the scene
       count, which agreed with the hydrated value only while max_scenes was
       unset (count 1) — set max_scenes to 6 and the same job carried 60
       in-session and 10 after a reload. A total, if a surface ever wants one,
       is duration × maxScenes computed where it is displayed. */
    requestedDurationSeconds: duration,
    aspectRatio: aspectRatio,
    metadata: {
      userId,
      sessionId: payload.sessionId,
      modelName: fields.model_name,
      duration,
      maxScenes,
      aspectRatio: aspectRatio,
      // Best-effort guess from the submitted model_name — the real value comes back
      // from the status poll immediately after (see fetchVideoStatus below).
      provider: fields.model_name.startsWith('wan')
        ? 'wan'
        : fields.model_name.startsWith('ltx')
          ? 'ltx'
          : 'kling',
      style: payload.style,
    },
  };
}
